/**
 * Multi-Broker Order Form (Web)
 * 3-step wizard for placing orders on multiple broker accounts
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only orchestrates order placement.
 * All order operations are handled by backend Order Service.
 */

import { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useMultiBrokerOrder } from '@shared/features/orders';
import { useBrokerAccountSelector } from '@shared/features/broker-accounts';
import { useInstrumentSearch } from '@shared/features/market-data';
import { formatCurrency } from '@shared/utils/formatters';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import type { Instrument } from '@shared/types/instrument';
import { extractOrderError, OrderErrorDetails } from '@shared/types/order';

const steps = ['Select Instrument', 'Order Details', 'Select Brokers'];

export function MultiBrokerOrderForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [instrumentId, setInstrumentId] = useState('');
  const [instrumentSymbol, setInstrumentSymbol] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState<Instrument | null>(null);
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [quantity, setQuantity] = useState('');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [price, setPrice] = useState('');
  const [timeInForce, setTimeInForce] = useState<'day' | 'gtc'>('day');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderResult, setOrderResult] = useState<{
    successes: any[];
    failures: any[];
    totalCount: number;
    successCount: number;
    failureCount: number;
  } | null>(null);
  const [errorDetails, setErrorDetails] = useState<OrderErrorDetails | null>(null);

  const { placeMultiBrokerOrder, placingOrder, progress, errors: orderErrors } = useMultiBrokerOrder();
  const { accounts, selectedAccounts, toggleAccount, selectAll, deselectAll } = useBrokerAccountSelector();
  const { search, results, loading: searchLoading, clear: clearSearch } = useInstrumentSearch();

  // Handle instrument search
  const handleInstrumentSearch = async (text: string) => {
    setInstrumentSymbol(text);
    if (text.trim().length >= 2) {
      await search(text.trim(), undefined, undefined, 10);
    } else {
      clearSearch();
      setSelectedInstrument(null);
      setInstrumentId('');
    }
  };

  // Handle instrument selection
  const handleSelectInstrument = (instrument: Instrument) => {
    setSelectedInstrument(instrument);
    setInstrumentSymbol(`${instrument.symbol}${instrument.exchange ? ` (${instrument.exchange})` : ''}`);
    setInstrumentId(instrument.instrument_id);
    clearSearch();
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (!instrumentId || !selectedInstrument) {
        setErrors({ instrument: 'Please search and select an instrument' });
        return;
      }
    } else if (activeStep === 1) {
      if (!quantity || parseFloat(quantity) <= 0) {
        setErrors({ quantity: 'Please enter a valid quantity' });
        return;
      }
      if (orderType === 'limit' && (!price || parseFloat(price) <= 0)) {
        setErrors({ price: 'Please enter a valid price for limit orders' });
        return;
      }
    } else if (activeStep === 2) {
      if (selectedAccounts.length === 0) {
        setErrors({ brokers: 'Please select at least one broker account' });
        return;
      }
    }

    setErrors({});
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setErrors({});
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePlaceOrder = async () => {
    setErrors({});
    setOrderResult(null);

    try {
      const result = await placeMultiBrokerOrder({
        instrument_id: instrumentId,
        side,
        quantity,
        order_type: orderType,
        price: orderType === 'limit' ? price : undefined,
        time_in_force: timeInForce,
      });

      setOrderResult(result);
      setErrorDetails(null);
      
      // Display success messages from backend if available
      if (result.successCount > 0 && result.successes.length > 0) {
        const successMessage = result.successes[0]?.message;
        if (successMessage) {
          // Success message is available in order.message
          // Could be displayed in a success alert if needed
        }
      }
      
      // Store error details from failures
      if (result.failureCount > 0 && result.failures.length > 0) {
        setErrorDetails(result.failures[0]?.errorDetails || null);
      }
    } catch (error: unknown) {
      const orderError = extractOrderError(error);
      setErrors({ submit: orderError.message });
      setErrorDetails(orderError.details || null);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setInstrumentId('');
    setInstrumentSymbol('');
    setSelectedInstrument(null);
    setSide('buy');
    setQuantity('');
    setOrderType('market');
    setPrice('');
    setTimeInForce('day');
    setErrors({});
    setOrderResult(null);
    clearSearch();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Instrument
            </Typography>
            <TextField
              label="Search Instrument Symbol"
              value={instrumentSymbol}
              onChange={(e) => handleInstrumentSearch(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Type to search (e.g., INFY, RELIANCE)"
              error={!!errors.instrument}
              helperText={errors.instrument}
              required
            />
            {searchLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {results.length > 0 && !selectedInstrument && (
              <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                {results.map((instrument) => (
                  <Card
                    key={instrument.instrument_id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => handleSelectInstrument(instrument)}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle1">{instrument.symbol}</Typography>
                      {instrument.exchange && (
                        <Typography variant="body2" color="text.secondary">
                          {instrument.exchange}
                        </Typography>
                      )}
                      {instrument.name && (
                        <Typography variant="body2" color="text.secondary">
                          {instrument.name}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            {selectedInstrument && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`${selectedInstrument.symbol}${selectedInstrument.exchange ? ` (${selectedInstrument.exchange})` : ''}`}
                  onDelete={() => {
                    setSelectedInstrument(null);
                    setInstrumentId('');
                    setInstrumentSymbol('');
                  }}
                  color="primary"
                  icon={<CheckCircleIcon />}
                />
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Details
            </Typography>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Side</InputLabel>
              <Select value={side} onChange={(e) => setSide(e.target.value as 'buy' | 'sell')} label="Side">
                <MenuItem value="buy">Buy</MenuItem>
                <MenuItem value="sell">Sell</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              fullWidth
              margin="normal"
              required
              error={!!errors.quantity}
              helperText={errors.quantity}
              inputProps={{ min: 1, step: 1 }}
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Order Type</InputLabel>
              <Select
                value={orderType}
                onChange={(e) => {
                  setOrderType(e.target.value as 'market' | 'limit');
                  if (e.target.value === 'market') {
                    setPrice('');
                  }
                }}
                label="Order Type"
              >
                <MenuItem value="market">Market</MenuItem>
                <MenuItem value="limit">Limit</MenuItem>
              </Select>
            </FormControl>

            {orderType === 'limit' && (
              <TextField
                label="Price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                margin="normal"
                required
                error={!!errors.price}
                helperText={errors.price}
                inputProps={{ min: 0, step: 0.01 }}
              />
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>Time in Force</InputLabel>
              <Select value={timeInForce} onChange={(e) => setTimeInForce(e.target.value as 'day' | 'gtc')} label="Time in Force">
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="gtc">Good Till Cancel (GTC)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Broker Accounts ({selectedAccounts.length} selected)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button size="small" onClick={selectAll}>
                Select All
              </Button>
              <Button size="small" onClick={deselectAll}>
                Deselect All
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {accounts.map((account) => {
                const isSelected = selectedAccounts.some((acc) => acc.id === account.id);
                const accountProgress = progress[account.id];
                return (
                  <Card
                    key={account.id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                    }}
                    onClick={() => toggleAccount(account.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6">{account.broker_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {account.account_name || account.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Balance: {formatCurrency(account.balance)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {accountProgress === 'success' && <CheckCircleIcon color="success" />}
                          {accountProgress === 'error' && <ErrorIcon color="error" />}
                          {accountProgress === 'pending' && <CircularProgress size={20} />}
                          {isSelected && <Chip label="Selected" color="primary" size="small" />}
                        </Box>
                      </Box>
                      {orderErrors[account.id] && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {orderErrors[account.id]}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
            {errors.brokers && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors.brokers}
              </FormHelperText>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <AuthenticatedLayout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              Place Multi-Broker Order
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {orderResult ? (
              <Box sx={{ mt: 4 }}>
                <Alert
                  severity={orderResult.failureCount === 0 ? 'success' : orderResult.successCount === 0 ? 'error' : 'warning'}
                  sx={{ mb: 3 }}
                >
                  {orderResult.failureCount === 0
                    ? (orderResult.successes.length > 0 && orderResult.successes[0]?.message)
                      ? orderResult.successes[0].message
                      : `Orders placed successfully on ${orderResult.successCount} broker(s)!`
                    : orderResult.successCount === 0
                    ? (orderResult.failures.length > 0 && orderResult.failures[0]?.error)
                      ? orderResult.failures[0].error
                      : `Failed to place orders on all ${orderResult.failureCount} broker(s)`
                    : `Partial success: ${orderResult.successCount} succeeded, ${orderResult.failureCount} failed`}
                  {errorDetails?.nextSteps && (
                    <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                      <strong>Next steps:</strong> {errorDetails.nextSteps}
                    </Box>
                  )}
                </Alert>

                {orderResult.successes.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Successful Orders ({orderResult.successes.length})
                    </Typography>
                    {orderResult.successes.map((order) => (
                      <Alert key={order.platform_order_id} severity="success" sx={{ mb: 1 }}>
                        {order.message || `Order #${order.platform_order_id} placed successfully`}
                        {order.broker_order_id && (
                          <Box component="span" sx={{ ml: 1, fontSize: '0.875rem', opacity: 0.8 }}>
                            (Broker ID: {order.broker_order_id})
                          </Box>
                        )}
                      </Alert>
                    ))}
                  </Box>
                )}

                {orderResult.failures.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Failed Orders ({orderResult.failures.length})
                    </Typography>
                    {orderResult.failures.map((failure, index) => {
                      const failureErrorDetails = failure.errorDetails;
                      const severity = failureErrorDetails?.severity === 'HIGH' ? 'error' 
                        : failureErrorDetails?.severity === 'MEDIUM' ? 'warning' 
                        : 'error';
                      
                      return (
                        <Alert 
                          key={index} 
                          severity={severity} 
                          sx={{ mb: 1 }}
                          action={
                            failureErrorDetails?.canRetry ? (
                              <Button 
                                color="inherit" 
                                size="small" 
                                onClick={handlePlaceOrder}
                              >
                                Retry
                              </Button>
                            ) : undefined
                          }
                        >
                          <Box>
                            <Box component="strong">{failure.broker_name}:</Box> {failure.error}
                            {failureErrorDetails?.nextSteps && (
                              <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                                <strong>Next steps:</strong> {failureErrorDetails.nextSteps}
                              </Box>
                            )}
                          </Box>
                        </Alert>
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button variant="contained" onClick={handleReset}>
                    Place Another Order
                  </Button>
                  <Button variant="outlined" component="a" href="/orders">
                    View Order History
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {renderStepContent(activeStep)}

                {errors.submit && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {errors.submit}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBackIcon />}>
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handlePlaceOrder}
                      disabled={placingOrder || selectedAccounts.length === 0}
                      endIcon={placingOrder ? <CircularProgress size={20} /> : undefined}
                    >
                      {placingOrder ? 'Placing Orders...' : `Place Order on ${selectedAccounts.length} Broker(s)`}
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                      Next
                    </Button>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </AuthenticatedLayout>
  );
}

